"use client";

import { useEffect } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useHreflang } from "@/i18n/useHreflang";
import HeroSection from "./components/HeroSection";
import StatsSection from "./components/StatsSection";
import ServicesSection from "./components/ServicesSection";
import OurScopeSection from "./components/OurScopeSection";
import ServiceAreasPreviewSection from "./components/ServiceAreasPreviewSection";
import MapSection from "@/components/feature/MapSection";
import WhyUsSection from "./components/WhyUsSection";
import FaqSection from "@/components/feature/FaqSection";
import BottomCtaSection from "./components/BottomCtaSection";
import LeadFormSection from "@/components/feature/LeadFormSection";
import TestimonialsSection from "./components/TestimonialsSection";
import { injectMetaTags } from "@/lib/seoHelpers";

export default function Home() {
  const { t, currentLang } = useLanguage();
  useHreflang();

  const homeFaqs = [
    {
      question: t("home.faq1Q") as string,
      answer: t("home.faq1A") as string,
    },
    {
      question: t("home.faq2Q") as string,
      answer: t("home.faq2A") as string,
    },
    {
      question: t("home.faq3Q") as string,
      answer: t("home.faq3A") as string,
    },
    {
      question: t("home.faq4Q") as string,
      answer: t("home.faq4A") as string,
    },
    {
      question: t("home.faq5Q") as string,
      answer: t("home.faq5A") as string,
    },
    {
      question: t("home.faq6Q") as string,
      answer: t("home.faq6A") as string,
    },
  ];

  useEffect(() => {
    document.title =
      currentLang === "es"
        ? "Reparación Honesta de Ventanas en Miami — Especialistas el Mismo Día"
        : "Honest Miami Window Repair — Same-Day Specialists";
  }, [currentLang]);

  useEffect(() => {
    const desc = document.querySelector('meta[name="description"]');
    const text =
      currentLang === "es"
        ? "Reparación honesta de ventanas en Miami en 12 vecindarios de Miami-Dade. Herrajes, vidrios, burletes, tapiado de emergencia. Servicio el mismo día."
        : "Honest Miami window repair across 12 Miami-Dade neighborhoods. Hardware, glass, weatherstripping, emergency board-up. Same-day service.";
    if (desc) desc.setAttribute("content", text);
  }, [currentLang]);

  useEffect(() => {
    const cleanupSeo = injectMetaTags({
      title:
        currentLang === "es"
          ? "Reparación Honesta de Ventanas en Miami — Especialistas el Mismo Día"
          : "Honest Miami Window Repair — Same-Day Specialists",
      description:
        currentLang === "es"
          ? "Reparación honesta de ventanas en Miami en 12 vecindarios de Miami-Dade. Herrajes, vidrios, burletes, tapiado de emergencia. Servicio el mismo día."
          : "Honest Miami window repair across 12 Miami-Dade neighborhoods. Hardware, glass, weatherstripping, emergency board-up. Same-day service.",
      url: "https://impact-window-repair-miami.com/",
      image: "https://impact-window-repair-miami.com/og/og-home.jpg",
      imageAlt: t("home.ogImageAlt") as string,
      type: "website",
    });

    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: "Windows and Doors Repair LLC",
      telephone: "+17863774452",
      email: "contact@impact-window-repair-miami.com",
      url: "https://impact-window-repair-miami.com",
      inLanguage: currentLang === "es" ? "es-US" : "en-US",
      address: {
        "@type": "PostalAddress",
        streetAddress: "848 Brickell Ave, PH-5",
        addressLocality: "Miami",
        addressRegion: "FL",
        postalCode: "33131",
        addressCountry: "US",
      },
      geo: {
        "@type": "GeoCoordinates",
        "latitude": 25.7617,
        "longitude": -80.1918
      },
      openingHoursSpecification: [{
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        "opens": "07:00",
        "closes": "21:00"
      }],
      priceRange: "$$",
      paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
      currenciesAccepted: "USD",
      areaServed: [
        {"@type": "City", "name": "Miami"},
        {"@type": "AdministrativeArea", "name": "Miami-Dade County"},
        "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove", "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne", "South Miami", "North Miami Beach"
      ],
      knowsLanguage: ["en", "es"],
      knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
      slogan: "Honest Miami Window Repair. Same-Day. 30-Day Guarantee.",
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Window Repair Services",
        itemListElement: [
          {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Sliding Door Repair"}, "priceCurrency": "USD", "priceSpecification": {"@type": "PriceSpecification", "minPrice": 120, "maxPrice": 280}},
          {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Window Hardware Repair"}, "priceCurrency": "USD", "priceSpecification": {"@type": "PriceSpecification", "minPrice": 120, "maxPrice": 280}},
          {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Glass Replacement"}, "priceCurrency": "USD", "priceSpecification": {"@type": "PriceSpecification", "minPrice": 180, "maxPrice": 450}},
          {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Weatherstripping & Sealing"}, "priceCurrency": "USD", "priceSpecification": {"@type": "PriceSpecification", "minPrice": 60, "maxPrice": 95}},
          {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Window Cleaning & Maintenance"}, "priceCurrency": "USD", "price": 129},
          {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Emergency Board-Up"}, "priceCurrency": "USD", "priceSpecification": {"@type": "PriceSpecification", "minPrice": 149}}
        ]
      },
    };

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: homeFaqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    };

    const script = document.createElement("script");
    script.setAttribute("type", "application/ld+json");
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    const faqScript = document.createElement("script");
    faqScript.setAttribute("type", "application/ld+json");
    faqScript.id = "faq-schema";
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      cleanupSeo();
      document.head.removeChild(script);
      document.head.removeChild(faqScript);
    };
  }, [currentLang]);

  return (
    <div className="font-roboto">
      <HeroSection />
      <LeadFormSection />
      <StatsSection />
      <ServicesSection />
      <OurScopeSection />
      <ServiceAreasPreviewSection />
      <MapSection />
      <WhyUsSection />
      <TestimonialsSection />
      <FaqSection items={homeFaqs} heading={t("home.faqTitle") as string} />
      <BottomCtaSection />
    </div>
  );
}